import { Output } from '../output/index.js';
import { Recording } from '../recording/index.js';
// import { RTMP } from '../rtmp/index.js';
import { TabsPanel } from '#~/common/tabs-panel/index.js';
import { DataSTag, Sel, STagNew } from '#~/stag/index.js';
import { Dismiss } from '@studio/design-system/dialog';
import { PostHogFeatureFlag, useFeatureFlags } from '#~/posthog/index.js';
import type { Agent } from '@studio/models/agent';
import type { Device } from '@studio/models/device';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { useMemo, type ReactElement } from 'react';
import { twMerge } from 'cn';

export interface ScaffoldProps {
  defaultTab?: 'recording' | 'output';
  defaultOutputTab?: 'ndi-output' | undefined;
  agentId: T_Maybe<Agent['id']>;
  selectedVideoDevice: T_Maybe<Device['id']>;
}

export const Scaffold = ({
  defaultTab,
  defaultOutputTab,
  agentId,
  selectedVideoDevice
}: ScaffoldProps): ReactElement => {
  const { flags: outputFlags } = useFeatureFlags([
    PostHogFeatureFlag.RECORDING_FEATURE,
    PostHogFeatureFlag.NDI_OUTPUT_FEATURE,
    PostHogFeatureFlag.RTMP_OUTPUT_FEATURE
  ]);

  const isRecordingEnabled = outputFlags[PostHogFeatureFlag.RECORDING_FEATURE];
  const isNDIEnabled = outputFlags[PostHogFeatureFlag.NDI_OUTPUT_FEATURE];
  const isRTMPEnabled = outputFlags[PostHogFeatureFlag.RTMP_OUTPUT_FEATURE];

  const isAnyOutputEnabled = isNDIEnabled || isRTMPEnabled;

  // Build tabs dynamically
  const tabs = useMemo(() => {
    const tabsList = [];

    // Each trigger carries an STag so HIL specs switch tabs by tag rather than
    // by role+label. Per rule: playwright-stag-selectors-only (ai.knowledge/platform).
    if (isRecordingEnabled) {
      tabsList.push({
        value: 'recording',
        label: 'Recording Settings',
        content: <Recording />,
        triggerStag: STagNew(Sel.Production, 'tab', 'recording')
      });
    }

    if (isAnyOutputEnabled) {
      tabsList.push({
        value: 'output',
        label: 'Output Settings',
        content: (
          <Output
            agentId={agentId}
            selectedVideoDevice={selectedVideoDevice}
            defaultOutputTab={defaultOutputTab}
          />
        ),
        triggerStag: STagNew(Sel.Production, 'tab', 'output')
      });
    }

    return tabsList;
  }, [isRecordingEnabled, isAnyOutputEnabled, agentId, selectedVideoDevice, defaultOutputTab]);

  // Determine dialog title
  const getTitle = (): string => {
    if (isRecordingEnabled && isAnyOutputEnabled) {
      return 'Recording and Output';
    }
    if (isRecordingEnabled) return 'Recording';
    if (isAnyOutputEnabled) return 'Output';
    return 'Production Settings';
  };

  // Safety: if no features enabled, show message
  if (tabs.length === 0) {
    return (
      <div
        className={twMerge('flex h-full w-sheet flex-col pt-[50px]')}
        {...DataSTag(Sel.Production, 'sheet')}
      >
        <div className='flex grow gap-28 px-40 text-md'>
          <div className='text-xl font-demibold'>Production Settings</div>
          <div className='flex-1' />
          <div className='flex flex-col items-start'>
            <Dismiss className='h-32 w-32' />
          </div>
        </div>
        <div className='px-40 py-32 text-center text-muted'>
          <p>No production features are currently enabled.</p>
          <p className='mt-8 text-sm'>Contact your administrator to enable features.</p>
        </div>
      </div>
    );
  }

  // Determine default tab intelligently
  const getDefaultTab = (): string => {
    // If defaultTab is specified and that feature is enabled, use it
    if (defaultTab === 'recording' && isRecordingEnabled) return 'recording';
    if (defaultTab === 'output' && isAnyOutputEnabled) return 'output';

    // Otherwise, use first available tab
    return tabs[0]!.value;
  };

  return (
    // The sheet root carries the STag HIL specs scope every production-sheet
    // query to, replacing getByRole('dialog').filter({ hasText: 'Select sources' }).
    <div
      className={twMerge('flex h-full w-sheet flex-col pt-[50px]')}
      {...DataSTag(Sel.Production, 'sheet')}
    >
      <div className='flex grow gap-28 px-40 text-md'>
        <div className='text-xl font-demibold'>{getTitle()}</div>

        <div className='flex-1' />

        <div className='flex flex-col items-start'>
          <Dismiss className='h-32 w-32' />
        </div>
      </div>

      <TabsPanel
        defaultValue={getDefaultTab()}
        className='mt-28'
        listClassName='px-40 text-md'
        tabs={tabs}
      />
    </div>
  );
};
